import { useState } from 'react'
import { CohortDrawer } from './drawer/CohortDrawer'
import {
  usePopulationMeasures,
  usePopulationProviders,
  usePopulationSummary,
  type CohortTarget,
} from './hooks'
import { CareGapTable } from './sections/CareGapTable'
import { FamilyCards } from './sections/FamilyCards'
import { FilterBar } from './sections/FilterBar'
import { FocusNow } from './sections/FocusNow'
import { HeadlineMetrics } from './sections/HeadlineMetrics'
import { Freshness, PeriodBar } from './sections/PeriodBar'
import { ProviderPerformance } from './sections/ProviderPerformance'
import { FILTER_KEYS, usePopulationParams } from './usePopulationParams'

/**
 * Population Health dashboard (PD-1774) on the PD-1715 analytics API (docs/POPULATION.md).
 * The period, comparison and filters live in the URL and feed every section and the cohort
 * drawer; each section loads on its own.
 */
export function PopulationPage() {
  const { query, setPeriod, setParam, clearFilters, activeFilterCount } = usePopulationParams()
  const [cohort, setCohort] = useState<CohortTarget | null>(null)
  const filterKey = FILTER_KEYS.map((key) => query[key] ?? '').join('|')

  const summary = usePopulationSummary(query)
  const measures = usePopulationMeasures(query)
  const providers = usePopulationProviders(query)

  return (
    <div className="flex min-h-full flex-col gap-6 bg-slate-50 p-6">
      <header className="flex flex-col gap-4">
        <PeriodBar
          query={query}
          window={summary.data?.window}
          stale={summary.isPlaceholderData}
          setPeriod={setPeriod}
          setParam={setParam}
        />
        <FilterBar
          query={query}
          setParam={setParam}
          clearFilters={clearFilters}
          activeFilterCount={activeFilterCount}
          trailing={<Freshness window={summary.data?.window} />}
        />
      </header>

      <HeadlineMetrics summary={summary} comparison={query.comparison} />
      <FamilyCards query={query} measures={measures} onOpenCohort={setCohort} />
      <FocusNow measures={measures} onOpenCohort={setCohort} />
      <ProviderPerformance
        providers={providers}
        // Only the current selection's median: the previous one would mis-flag providers.
        populationMedianDays={
          summary.isPlaceholderData ? undefined : summary.data?.closureTime.current.medianDays
        }
        selectedProviderId={query.providerId}
        onSelectProvider={(providerId) => setParam('providerId', providerId)}
      />
      <CareGapTable measures={measures} onOpenCohort={setCohort} />

      <CohortDrawer
        // Remounted per cohort and per page filter set: the page filters can change under an open
        // drawer (via history), and its own filters and card totals must start again from them.
        key={cohort ? `${cohort.type}:${cohort.slug}:${filterKey}` : 'closed'}
        target={cohort}
        query={query}
        onClose={() => setCohort(null)}
      />
    </div>
  )
}
